import React from 'react';
import { fitLevel, FIT_STROKE, TRACK_COLOR } from '../../lib/fit';

interface ScoreRingProps {
  /** 0–100 BidWin Score. */
  score: number;
  /** Diameter in px. Verdict block 66, proposal cards 46–52, completeness 40–44. */
  size?: number;
  /** Show the small "/100" scale under the number. */
  showScale?: boolean;
  /** Override the arc/number color (e.g. brand primary for profile completeness). */
  color?: string;
  className?: string;
  'aria-label'?: string;
}

/**
 * Circular SVG gauge — the arc is score/100 in the fit color, the number is
 * centered in Roboto Mono. Used in the Verdict Block, proposal cards, and the
 * profile completeness meter.
 */
export function ScoreRing({
  score,
  size = 64,
  showScale = false,
  color,
  className = '',
  'aria-label': ariaLabel,
}: ScoreRingProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const stroke = size > 40 ? 5 : 4;
  const radius = size / 2 - 5;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - clamped / 100);
  const arcColor = color ?? FIT_STROKE[fitLevel(clamped)];

  return (
    <div
      className={`relative flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
      role="img"
      aria-label={ariaLabel ?? `BidWin Score ${clamped} out of 100`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)' }}
        aria-hidden="true"
      >
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={TRACK_COLOR} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={arcColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-mono font-medium leading-none"
          style={{ fontSize: size * 0.34, color: arcColor }}
        >
          {clamped}
        </span>
        {showScale && (
          <span className="text-bw-muted leading-none mt-0.5" style={{ fontSize: 8, letterSpacing: '0.05em' }}>
            /100
          </span>
        )}
      </div>
    </div>
  );
}

export default ScoreRing;
